import { ITEMS_PER_PAGE } from '@/constants';
import i18n from '@/i18n';
import { default as apiService } from '@/services/api.ts';
import { useAuthStore } from '@/stores/auth';
import { useSortingStore } from '@/stores/sorting';
import { useToastStore } from '@/stores/toast';
import type { Project, ProjectFilter } from '@/types';
import { log } from '@/utils/log';
import { readPageSize, storePageSize } from '@/utils/pageSize';
import { defineStore } from 'pinia';
import { ref, watch } from 'vue';

export const useProjectsStore = defineStore('projects', () => {
    const authStore = useAuthStore();
    const toast = useToastStore();
    const sortStore = useSortingStore('projects');

    const models = ref<Project[]>([]);
    const currentPage = ref(1);
    const totalPages = ref(1);
    const totalItems = ref(0);
    const itemsPerPage = ref(ITEMS_PER_PAGE);
    const isLoading = ref(false);
    let latestListRequest = 0;

    const filter = ref<ProjectFilter>({
        name: null,
        customer_id: null,
        archived: null,
    });

    watch(
        () => authStore.activeCompany?.id,
        async (newId, oldId) => {
            if (newId !== oldId && newId) {
                await loadPage(1);
            }
        },
    );

    async function loadPage(page: number) {
        const requestId = ++latestListRequest;
        const requestedFilter = { ...filter.value };
        isLoading.value = true;
        try {
            const activeCompanyId = authStore.activeCompany?.id;
            // Strip null values so we don't send empty params; archived=null means show all
            const filterCleaned = Object.fromEntries(Object.entries(requestedFilter).filter(([, v]) => v !== null));
            const params = activeCompanyId ? { ...filterCleaned, company_id: activeCompanyId } : filterCleaned;

            const response = await apiService.project.index(
                page,
                params,
                sortStore.currentSort,
                readPageSize('projects'),
            );
            if (requestId !== latestListRequest || JSON.stringify(requestedFilter) !== JSON.stringify(filter.value)) {
                return;
            }
            models.value = response.data.data;
            currentPage.value = response.data.meta.current_page;
            totalPages.value = response.data.meta.last_page;
            totalItems.value = response.data.meta.total;
            itemsPerPage.value = response.data.meta.per_page;
        } catch (error) {
            if (requestId === latestListRequest) {
                log('Failed to load projects:', error);
            }
        } finally {
            if (requestId === latestListRequest) {
                isLoading.value = false;
            }
        }
    }

    async function applyFilter() {
        await loadPage(1);
    }

    async function resetFilter() {
        filter.value = { name: null, customer_id: null, archived: null };
        await loadPage(1);
    }

    function isFiltered(): boolean {
        return filter.value.name !== null || filter.value.customer_id !== null || filter.value.archived !== null;
    }

    async function deleteProject(id: string) {
        try {
            await apiService.project.delete(id);
            toast.success(i18n.global.t('projects.projectDeleted'));
        } catch (error) {
            log('Failed to delete project:', error);
            toast.error(i18n.global.t('error.generic'));
            throw error;
        }
    }

    async function archiveProject(id: string) {
        try {
            const response = await apiService.project.archive(id);
            toast.success(i18n.global.t('projects.projectArchived'));
            return response.data.data;
        } catch (error) {
            log('Failed to archive project:', error);
            toast.error(i18n.global.t('error.generic'));
            throw error;
        }
    }

    async function unarchiveProject(id: string) {
        try {
            const response = await apiService.project.unarchive(id);
            toast.success(i18n.global.t('projects.projectUnarchived'));
            return response.data.data;
        } catch (error) {
            log('Failed to unarchive project:', error);
            toast.error(i18n.global.t('error.generic'));
            throw error;
        }
    }

    async function getAllActive(): Promise<Project[]> {
        try {
            const activeCompanyId = authStore.activeCompany?.id;
            const response = await apiService.project.index(1, {
                company_id: activeCompanyId,
                archived: false,
                per_page: 100,
            });
            return response.data.data;
        } catch (error) {
            log('Failed to fetch active projects:', error);
            return [];
        }
    }

    /** Switches this list to another page size, remembers it and reloads from page 1. */
    async function setItemsPerPage(size: number) {
        if (!storePageSize('projects', size)) return;
        itemsPerPage.value = size;
        await loadPage(1);
    }

    return {
        models,
        filter,
        sortStore,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        setItemsPerPage,
        isLoading,
        loadPage,
        applyFilter,
        resetFilter,
        isFiltered,
        deleteProject,
        archiveProject,
        unarchiveProject,
        getAllActive,
    };
});
